import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { X, Eye, Mail, BarChart3, Send, Loader2, Plus, Trash2, Undo2 } from 'lucide-react';
import { ProposalNodeSet, GlobalCosts, ProposalBuilderData, ProposalDiscount } from '../../types';
import {
  applyProfitMargin,
  calculateNodeSetTotal,
  getEffectiveProjectOverheadPercent,
  getProposalDiscountAmount,
} from '../../utils/proposalCalculations';
import {
  computeExclusiveStaffEconomics,
  exclusivePoolShare,
} from '../../utils/proposalExclusiveEconomics';
import { structureIsExclusive } from '../../utils/proposalExclusiveGroups';
import { computeVersionSpeciesPricingRows } from '../../utils/proposalVersionSpeciesPricing';
import './ProposalReviewModal.css';

interface ProposalReviewModalProps {
  open: boolean;
  onClose: () => void;
  onSend: (customMessage: string, recipients: string[]) => Promise<void>;
  clientName: string;
  clientEmail: string;
  proposalSlug: string;
  nodeSets: ProposalNodeSet[];
  profitMargin: number;
  sending: boolean;
  savedMessage: string;
  onMessageChange: (msg: string) => void;
  savedRecipients: string[];
  onRecipientsChange: (recipients: string[]) => void;
  /** Matches proposal builder footer (global costs + project overhead %). */
  globalCosts?: GlobalCosts | null;
  isKitProposal?: boolean;
  kitPricing?: Array<{ speciesName: string; totalCost: number }>;
  activeSpecies?: string;
  /** Promotional discount (flat dollars off the post-margin client total). */
  discount?: ProposalDiscount;
  /** Full frozen proposal — used in snapshot mode for multi-species pricing. */
  snapshotProposal?: ProposalBuilderData;
  /** Read-only historical snapshot: internal summary only, no send/email/client preview. */
  snapshotMode?: boolean;
  /** Extra line under the title when snapshotMode (e.g. date + label). */
  snapshotSubtitle?: string;
}

type Tab = 'internal' | 'client' | 'email';

const COUNTDOWN_SECONDS = 7;
const ALL_TABS: Tab[] = ['internal', 'client', 'email'];

const fmt = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });

const pct = (cost: number, billable: number) => {
  if (!cost || cost === 0) return '—';
  const margin = ((billable - cost) / billable) * 100;
  return `${margin.toFixed(1)}%`;
};

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const ProposalReviewModal: React.FC<ProposalReviewModalProps> = ({
  open,
  onClose,
  onSend,
  clientName,
  clientEmail,
  proposalSlug,
  nodeSets,
  profitMargin,
  sending,
  savedMessage,
  onMessageChange,
  savedRecipients,
  onRecipientsChange,
  globalCosts,
  isKitProposal,
  kitPricing,
  activeSpecies,
  discount,
  snapshotProposal,
  snapshotMode,
  snapshotSubtitle,
}) => {
  const [activeTab, setActiveTab] = useState<Tab>('internal');
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const addInputRef = useRef<HTMLInputElement>(null);

  // Tab gate: track which tabs have been visited
  const [visitedTabs, setVisitedTabs] = useState<Set<Tab>>(() => new Set<Tab>(['internal']));
  const allTabsVisited = ALL_TABS.every(t => visitedTabs.has(t));

  const handleTabClick = useCallback((tab: Tab) => {
    setActiveTab(tab);
    setVisitedTabs(prev => {
      if (prev.has(tab)) return prev;
      const next = new Set(prev);
      next.add(tab);
      return next;
    });
  }, []);

  // Countdown state
  const [countdown, setCountdown] = useState<number | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownMessageRef = useRef('');
  const countdownRecipientsRef = useRef<string[]>([]);

  const clearCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setCountdown(null);
  }, []);

  const handleStartCountdown = useCallback(() => {
    countdownMessageRef.current = savedMessage;
    countdownRecipientsRef.current = [...savedRecipients];
    setCountdown(COUNTDOWN_SECONDS);
  }, [savedMessage, savedRecipients]);

  useEffect(() => {
    if (countdown === null) return;
    if (countdown <= 0) {
      clearCountdown();
      onSend(countdownMessageRef.current, countdownRecipientsRef.current);
      return;
    }
    countdownRef.current = setInterval(() => {
      setCountdown(prev => (prev !== null ? prev - 1 : null));
    }, 1000);
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [countdown, clearCountdown, onSend]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  const effectiveDiscount = snapshotMode
    ? snapshotProposal?.discount
    : discount ?? snapshotProposal?.discount;
  const discountAmount = getProposalDiscountAmount(effectiveDiscount);

  const exclusiveStaff = useMemo(
    () =>
      computeExclusiveStaffEconomics(nodeSets, profitMargin, globalCosts, {
        isKitProposal,
        kitPricing,
        activeSpecies,
        discountAmount,
        designEstimating: snapshotProposal?.designEstimating,
        proposalStatus: snapshotProposal?.status,
      }),
    [nodeSets, profitMargin, globalCosts, isKitProposal, kitPricing, activeSpecies, discountAmount, snapshotProposal?.designEstimating, snapshotProposal?.status]
  );
  const summaryEconomics = exclusiveStaff.defaultEconomics;
  const defaultShare = useMemo(
    () => exclusivePoolShare(exclusiveStaff.defaultNodeSets, exclusiveStaff.defaultEconomics),
    [exclusiveStaff],
  );

  /** Splits follow the selected Choose one set (same as the public proposal). */
  const allocationSplit = useMemo(() => {
    const overheadPct = getEffectiveProjectOverheadPercent(globalCosts);
    return {
      globalCostsTotal: summaryEconomics.globalCostTotal,
      projectOverheadAmount: summaryEconomics.projectOverheadAmount,
      globalAddonPerStructure: defaultShare.globalAddonPerStructure,
      projectOverheadPerStructure: defaultShare.projectOverheadPerStructure,
      overheadPct,
      enabledCount: exclusiveStaff.defaultNodeSets.length || 1,
    };
  }, [summaryEconomics, defaultShare, globalCosts, exclusiveStaff.defaultNodeSets.length]);

  const structureSubtotalRaw = useMemo(
    () =>
      exclusiveStaff.defaultNodeSets.reduce(
        (sum, ns) => sum + calculateNodeSetTotal(ns) * (ns.quantity || 1),
        0,
      ),
    [exclusiveStaff.defaultNodeSets]
  );

  const grandInstallBillable = exclusiveStaff.defaultNodeSets.reduce((sum, ns) => {
    if (ns.excludeInstall) return sum;
    const qty = ns.quantity || 1;
    return (
      sum +
      applyProfitMargin(((ns.install?.total || 0) + (ns.install?.overheadTotal || 0)) * qty, profitMargin)
    );
  }, 0);

  const fullRawCost = summaryEconomics.rawCostTotal;
  const clientPriceTotal = Math.round(summaryEconomics.billableTotal);
  const displayProfit = Math.round(clientPriceTotal - fullRawCost);
  const displayMarginPct = clientPriceTotal > 0 ? (displayProfit / clientPriceTotal) * 100 : 0;

  const speciesPricingRows = useMemo(() => {
    if (!snapshotMode || !snapshotProposal) return [];
    return computeVersionSpeciesPricingRows(snapshotProposal);
  }, [snapshotMode, snapshotProposal]);

  const activeSpeciesRow = speciesPricingRows.find((r) => r.isActive) ?? speciesPricingRows[0];
  const snapshotClientPrice =
    snapshotMode && activeSpeciesRow ? activeSpeciesRow.billableTotal : clientPriceTotal;
  const snapshotDisplayProfit = Math.round(snapshotClientPrice - fullRawCost);
  const snapshotDisplayMarginPct =
    snapshotClientPrice > 0 ? (snapshotDisplayProfit / snapshotClientPrice) * 100 : 0;

  if (!open) return null;

  const isSnapshot = !!snapshotMode;
  const recipients = savedRecipients;
  const customMessage = savedMessage;

  const handleAddEmail = () => {
    const email = newEmail.trim();
    if (!email) return;
    if (!isValidEmail(email)) {
      setEmailError('Invalid email address');
      return;
    }
    if (recipients.includes(email)) {
      setEmailError('Already added');
      return;
    }
    onRecipientsChange([...recipients, email]);
    setNewEmail('');
    setEmailError('');
  };

  const handleRemoveEmail = (email: string) => {
    onRecipientsChange(recipients.filter(r => r !== email));
  };

  const handleAddKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const companyName = 'LC Timber Frames';

  const emailHtml = `
    <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 560px; margin: 0 auto; padding: 32px 24px;">
      <div style="background: #2C3E2F; padding: 24px; border-radius: 10px 10px 0 0; text-align: center;">
        <h1 style="color: white; margin: 0; font-size: 20px; letter-spacing: 1px;">${companyName}</h1>
        <p style="color: rgba(255,255,255,0.7); margin: 4px 0 0; font-size: 12px;">CLIENT PORTAL</p>
      </div>
      <div style="background: white; padding: 28px 24px; border: 1px solid #E2E0DB; border-top: none; border-radius: 0 0 10px 10px;">
        <p style="color: #4F423A; line-height: 1.6; margin: 0 0 20px; white-space: pre-wrap;">${customMessage.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br/>')}</p>
        <div style="background: #F7F5F0; border: 1px solid #E2E0DB; border-radius: 8px; padding: 16px 20px; margin: 0 0 20px;">
          <p style="margin: 0 0 4px; font-size: 13px; color: #6B6960;">Your portal access PIN:</p>
          <p style="margin: 0; font-size: 22px; font-weight: 700; color: #2C3E2F; letter-spacing: 3px;">••••</p>
        </div>
        <a href="#" style="display: inline-block; padding: 14px 32px; background: #2C3E2F; color: white; text-decoration: none; border-radius: 6px; font-weight: 600; font-size: 14px;">
          View Your Proposal
        </a>
        <p style="color: #9C9889; font-size: 12px; margin: 20px 0 0;">
          You'll need the PIN above to access your portal.
        </p>
      </div>
    </div>
  `;

  return (
    <div className="prm-overlay" onClick={onClose}>
      <div className="prm-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="prm-header">
          <div>
            <h2>{isSnapshot ? 'Proposal snapshot (read-only)' : 'Review & Send Proposal'}</h2>
            {isSnapshot && snapshotSubtitle && (
              <p className="prm-snapshot-subtitle">{snapshotSubtitle}</p>
            )}
          </div>
          <button className="prm-close" onClick={onClose}><X size={20} /></button>
        </div>

        {/* Tabs */}
        {!isSnapshot && (
          <div className="prm-tabs">
            <button className={`prm-tab ${activeTab === 'internal' ? 'active' : ''}`} onClick={() => handleTabClick('internal')}>
              <BarChart3 size={15} /> Internal Summary
              {!visitedTabs.has('internal') && <span className="prm-tab-dot" />}
            </button>
            <button className={`prm-tab ${activeTab === 'client' ? 'active' : ''}`} onClick={() => handleTabClick('client')}>
              <Eye size={15} /> Client View
              {!visitedTabs.has('client') && <span className="prm-tab-dot" />}
            </button>
            <button className={`prm-tab ${activeTab === 'email' ? 'active' : ''}`} onClick={() => handleTabClick('email')}>
              <Mail size={15} /> Email Preview
              {!visitedTabs.has('email') && <span className="prm-tab-dot" />}
            </button>
          </div>
        )}

        {/* Tab content */}
        <div className="prm-body">
          {(isSnapshot || activeTab === 'internal') && (
            <div className="prm-internal">
              <div className="prm-summary-banner">
                <div className="prm-summary-stat">
                  <span className="prm-stat-label">Total Cost</span>
                  <span className="prm-stat-value">{fmt(fullRawCost)}</span>
                </div>
                <div className="prm-summary-stat">
                  <span className="prm-stat-label">Client Price</span>
                  <span className="prm-stat-value prm-highlight">
                    {fmt(isSnapshot ? snapshotClientPrice : clientPriceTotal)}
                  </span>
                </div>
                <div className="prm-summary-stat">
                  <span className="prm-stat-label">Profit</span>
                  <span className="prm-stat-value prm-profit">
                    {fmt(isSnapshot ? snapshotDisplayProfit : displayProfit)}
                  </span>
                </div>
                <div className="prm-summary-stat">
                  <span className="prm-stat-label">Margin</span>
                  <span className="prm-stat-value">
                    {(isSnapshot ? snapshotDisplayMarginPct : displayMarginPct).toFixed(1)}%
                  </span>
                </div>
                <div className="prm-summary-stat">
                  <span className="prm-stat-label">Install (billable)</span>
                  <span className="prm-stat-value">{fmt(grandInstallBillable)}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="prm-summary-stat">
                    <span className="prm-stat-label">
                      {effectiveDiscount?.label || 'Discount'}
                    </span>
                    <span className="prm-stat-value" style={{ color: '#15803d' }}>
                      −{fmt(discountAmount)}
                    </span>
                  </div>
                )}
              </div>

              {isSnapshot && speciesPricingRows.length > 0 && (
                <div className="prm-species-section">
                  <div className="prm-species-header">
                    <h3>Wood species pricing</h3>
                    {isKitProposal && (
                      <span className="prm-species-kit-badge">Kit proposal</span>
                    )}
                  </div>
                  <p className="prm-species-intro">
                    {speciesPricingRows.length > 1
                      ? 'Client price options offered at this version. The active species drives the summary client price above.'
                      : 'Timber species for this version.'}
                  </p>
                  <ul className="prm-species-list">
                    {speciesPricingRows.map((row) => (
                      <li
                        key={row.speciesName}
                        className={`prm-species-item${row.isActive ? ' prm-species-item--active' : ''}`}
                      >
                        <div className="prm-species-item-main">
                          <span className="prm-species-item-name">{row.speciesName}</span>
                          <span className="prm-species-item-price">{fmt(row.billableTotal)}</span>
                        </div>
                        <div className="prm-species-item-meta">
                          {row.isActive && (
                            <span className="prm-species-status prm-species-status--active">Active</span>
                          )}
                          {row.isDefault && !row.isActive && (
                            <span className="prm-species-status prm-species-status--default">Default</span>
                          )}
                          {!row.isActive && !row.isDefault && (
                            <span className="prm-species-status">Offered</span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="prm-table-wrap">
                <table className="prm-table">
                  <thead>
                    <tr>
                      <th>Structure</th>
                      <th>Design</th>
                      <th>Materials</th>
                      <th>Production</th>
                      <th>Freight</th>
                      <th>Install</th>
                      <th>Travel</th>
                      <th>Overhead</th>
                      <th
                        className="prm-th-split"
                        title="Raw global project $ split among structures not marked “exclude from global cost split.”"
                      >
                        Global alloc.
                      </th>
                      <th
                        className="prm-th-split"
                        title={`Project overhead (${allocationSplit.overheadPct}% of structure raw + global) on the selected Choose one set — raw, before margin.`}
                      >
                        Proj. OH
                      </th>
                      <th title="Structure-only raw cost (sum of columns left of Global alloc.)">Struct. cost</th>
                      <th title="Structure raw + global alloc. + project OH slice (loaded raw for pricing)">Loaded cost</th>
                      <th>Client price</th>
                      <th title="Margin on client price using loaded cost">Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nodeSets.filter(ns => ns.enabled !== false).map(ns => {
                      const qty = ns.quantity || 1;
                      const design = (ns.design?.total || 0) * qty;
                      const materials = (ns.materials?.total || 0) * qty;
                      const production = (ns.production?.total || 0) * qty;
                      const freight = (ns.freight?.total || 0) * qty;
                      const install = ((ns.install?.total || 0) + (ns.install?.overheadTotal || 0)) * qty;
                      const travel = (ns.travel?.total || 0) * qty;
                      const overhead = (ns.overhead?.total || 0) * qty;
                      const structureRaw = calculateNodeSetTotal(ns) * qty;
                      const exclusiveChoice = exclusiveStaff.choices.find(c => c.id === ns.id);
                      const share = exclusiveChoice
                        ? exclusivePoolShare(exclusiveChoice.nodeSets, exclusiveChoice.economics)
                        : defaultShare;
                      const receivesGlobal = !ns.excludeFromGlobalPrices;
                      const globalAllocRaw =
                        share.globalAddonPerStructure > 0 && receivesGlobal
                          ? share.globalAddonPerStructure
                          : 0;
                      const poAllocRaw = share.projectOverheadPerStructure;
                      const loadedRaw = structureRaw + globalAllocRaw + poAllocRaw;
                      const billable = Math.round(applyProfitMargin(loadedRaw, profitMargin));
                      const unselectedExclusive =
                        structureIsExclusive(ns) && ns.id !== exclusiveStaff.defaultSelectedId;

                      return (
                        <tr
                          key={ns.id}
                          className={unselectedExclusive ? 'prm-exclusive-alt' : undefined}
                        >
                          <td className="prm-name-cell">
                            {ns.name}
                            {qty > 1 && <span className="prm-qty"> x{qty}</span>}
                            {ns.excludeFromGlobalPrices && (
                              <span className="prm-exclude-hint" title="Excluded from global $ split"> · no global</span>
                            )}
                            {ns.clientOptional && (
                              <span className="prm-exclude-hint" title="Client may opt out on public proposal"> · optional</span>
                            )}
                            {structureIsExclusive(ns) && (
                              <span
                                className="prm-exclude-hint"
                                title={
                                  unselectedExclusive
                                    ? 'As-if-selected price if the client picks this option'
                                    : 'Default Choose one pick — included in the project total'
                                }
                              >
                                {unselectedExclusive
                                  ? ' · Choose 1 — not in project total'
                                  : ' · Choose 1'}
                              </span>
                            )}
                          </td>
                          <td>{fmt(design)}</td>
                          <td>{fmt(materials)}</td>
                          <td>{fmt(production)}</td>
                          <td>{fmt(freight)}</td>
                          <td>{fmt(install)}</td>
                          <td>{fmt(travel)}</td>
                          <td>{fmt(overhead)}</td>
                          <td className="prm-split-cell">{fmt(globalAllocRaw)}</td>
                          <td className="prm-split-cell">{fmt(poAllocRaw)}</td>
                          <td className="prm-cost-cell">{fmt(structureRaw)}</td>
                          <td className="prm-cost-cell prm-loaded-cell">{fmt(loadedRaw)}</td>
                          <td className="prm-billable-cell">{fmt(billable)}</td>
                          <td className="prm-margin-cell">{pct(loadedRaw, billable)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="prm-totals-final">
                      <td><strong>Project total</strong></td>
                      <td colSpan={7}></td>
                      <td className="prm-split-cell"><strong>{fmt(allocationSplit.globalCostsTotal)}</strong></td>
                      <td className="prm-split-cell"><strong>{fmt(allocationSplit.projectOverheadAmount)}</strong></td>
                      <td className="prm-cost-cell"><strong>{fmt(structureSubtotalRaw)}</strong></td>
                      <td className="prm-cost-cell prm-loaded-cell"><strong>{fmt(fullRawCost)}</strong></td>
                      <td className="prm-billable-cell"><strong>{fmt(clientPriceTotal)}</strong></td>
                      <td className="prm-margin-cell"><strong>{displayMarginPct.toFixed(1)}%</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {exclusiveStaff.choices.length >= 2 && exclusiveStaff.billableRange && (
                <p className="prm-choose-one-note">
                  Choose one range {fmt(exclusiveStaff.billableRange.min)}–{fmt(exclusiveStaff.billableRange.max)}.
                  {' '}Project total uses {exclusiveStaff.choices[0].name} (first in builder order).
                </p>
              )}

              <p className="prm-margin-note">
                Profit margin setting: <strong>{profitMargin.toFixed(0)}%</strong>
                {' — '}
                Splits follow the selected Choose one set, same as the client.
                {' '}
                <strong>Global alloc.</strong> and <strong>Proj. OH</strong> on an exclusive row are as if that option were selected (full remaining globals and overhead on that job).
                Unselected exclusive options are listed for comparison and are not in the project total.
                {' '}
                <strong>Loaded cost</strong> is structure + global + OH.
                {' '}
                <strong>Client price</strong> is that loaded cost with margin; row <strong>margin</strong> uses loaded cost vs. client price.
              </p>
            </div>
          )}

          {!isSnapshot && activeTab === 'client' && (
            <div className="prm-client-view">
              <iframe
                src={`/proposal/${proposalSlug}?from=portal&portalSlug=preview`}
                title="Proposal Client Preview"
                className="prm-iframe"
              />
            </div>
          )}

          {!isSnapshot && activeTab === 'email' && (
            <div className="prm-email-tab">
              <div className="prm-email-meta">
                <div className="prm-email-field prm-email-to-field">
                  <label>To</label>
                  <div className="prm-recipients-wrap">
                    <div className="prm-recipient-chips">
                      {recipients.map(email => (
                        <span key={email} className={`prm-recipient-chip ${email === clientEmail ? 'primary' : ''}`}>
                          {email}
                          {email !== clientEmail && (
                            <button className="prm-chip-remove" onClick={() => handleRemoveEmail(email)} title="Remove">
                              <X size={12} />
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                    <div className="prm-add-recipient">
                      <input
                        ref={addInputRef}
                        type="email"
                        className="prm-add-email-input"
                        placeholder="Add email address..."
                        value={newEmail}
                        onChange={e => { setNewEmail(e.target.value); setEmailError(''); }}
                        onKeyDown={handleAddKeyDown}
                      />
                      <button className="prm-add-email-btn" onClick={handleAddEmail} disabled={!newEmail.trim()}>
                        <Plus size={14} /> Add
                      </button>
                    </div>
                    {emailError && <span className="prm-email-error">{emailError}</span>}
                  </div>
                </div>
                <div className="prm-email-field">
                  <label>Subject</label>
                  <span>Your proposal from {companyName} is ready</span>
                </div>
              </div>

              <div className="prm-email-edit">
                <label>Message (editable)</label>
                <textarea
                  value={customMessage}
                  onChange={e => onMessageChange(e.target.value)}
                  rows={6}
                  className="prm-email-textarea"
                />
              </div>

              <div className="prm-email-preview-wrap">
                <label>Email Preview</label>
                <div className="prm-email-preview" dangerouslySetInnerHTML={{ __html: emailHtml }} />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="prm-footer">
          {isSnapshot ? (
            <div className="prm-footer-actions prm-footer-snapshot">
              <button type="button" className="prm-btn-cancel" onClick={onClose}>
                Close
              </button>
            </div>
          ) : countdown !== null ? (
            <div className="prm-countdown-bar">
              <div className="prm-countdown-left">
                <svg className="prm-countdown-ring" viewBox="0 0 36 36" width="36" height="36">
                  <circle className="prm-ring-bg" cx="18" cy="18" r="15.5" fill="none" stroke="#E8E5E0" strokeWidth="3" />
                  <circle
                    className="prm-ring-progress"
                    cx="18" cy="18" r="15.5"
                    fill="none" stroke="#2C3E2F" strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 15.5}
                    strokeDashoffset={2 * Math.PI * 15.5 * (1 - countdown / COUNTDOWN_SECONDS)}
                    transform="rotate(-90 18 18)"
                  />
                </svg>
                <span className="prm-countdown-text">
                  Sending in <strong>{countdown}s</strong>
                </span>
              </div>
              <button className="prm-btn-undo" onClick={clearCountdown}>
                <Undo2 size={15} /> Undo
              </button>
            </div>
          ) : sending ? (
            <div className="prm-countdown-bar prm-sending-bar">
              <div className="prm-countdown-left">
                <Loader2 size={20} className="prm-spin" />
                <span className="prm-countdown-text">Sending proposal...</span>
              </div>
            </div>
          ) : (
            <>
              <div className="prm-footer-meta">
                <span className="prm-footer-client">{clientName}</span>
                <span className="prm-footer-email">{recipients.length} recipient{recipients.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="prm-footer-actions">
                <button className="prm-btn-cancel" onClick={onClose}>Cancel</button>
                {!allTabsVisited && (
                  <span className="prm-send-hint">Review all tabs before sending</span>
                )}
                <button
                  className="prm-btn-send"
                  onClick={handleStartCountdown}
                  disabled={!allTabsVisited}
                  title={!allTabsVisited ? 'Visit all 3 tabs to enable sending' : ''}
                >
                  <Send size={16} /> Send Proposal
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProposalReviewModal;
